import { keepPreviousData } from '@tanstack/react-query';
import { useApiQuery } from './useApiQuery';
import { useApiMutation } from './useApiMutation';
import { queryKeys } from './queryKeys';
import { pendingTasksService } from '@/services/pendingTasksService';
import { useSafeToast } from '@/hooks/useSafeToast';
import { APIError } from '@/utils/api-client';
import type { PendingTasksQuery } from '@/types/pendingTasks';

export function usePendingTasks(query: PendingTasksQuery) {
  return useApiQuery(
    queryKeys.pendingTasks.list(query),
    () => pendingTasksService.getPendingTasks(query),
    {
      staleTime: 30 * 1000,
      refetchInterval: 60 * 1000,
      // Keep the current page visible while the next page/sort loads.
      placeholderData: keepPreviousData,
    }
  );
}

export function usePendingTasksStatus() {
  return useApiQuery(
    queryKeys.pendingTasks.status(),
    () => pendingTasksService.getStatus(),
    {
      staleTime: 30 * 1000,
      refetchInterval: 60 * 1000,
    }
  );
}

export function useRefreshPendingTasks() {
  const { toast } = useSafeToast();
  return useApiMutation(() => pendingTasksService.refresh(), {
    invalidateQueries: [
      queryKeys.pendingTasks.lists(),
      queryKeys.pendingTasks.status(),
    ],
    showSuccessToast: false,
    showErrorToast: false,
    onError: (error) => {
      const refreshedRecently =
        error instanceof APIError && error.status === 429;
      toast({
        title: refreshedRecently ? 'Refreshed recently' : 'Error',
        description: refreshedRecently
          ? 'Try again shortly.'
          : "Couldn't refresh pending tasks",
        variant: 'destructive',
      });
    },
  });
}
